import { useCallback, useEffect, useRef, useState } from "react";
import {
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Field, Form, Formik } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import * as Yup from "yup";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import {
  FdpResponse,
  StoreFdpsResponse,
} from "../../../../app/models/responseModels/FdpResponse";
import {
  AdjustmentType,
  ClientInvoiceLce,
  ClientInvoiceOta,
  RatecardClientInvoiceResponse,
  StoreResponseForClientInvoice,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";
import agent from "../../../../app/api/agent";
import { toast } from "react-toastify";
import getAxiosError from "../../../../app/api/axiosError";
import { format, parse, parseISO } from "date-fns";
import dayjs from "dayjs";

interface LateCancelsAndAdjustmentsProps {
  invoice: RatecardClientInvoiceResponse | undefined;
  associatedStoreFdps: StoreFdpsResponse[];
  selectedStores: StoreResponseForClientInvoice[];
}

const LateCancelsAndAdjustments = ({
  invoice,
  selectedStores,
  associatedStoreFdps,
}: LateCancelsAndAdjustmentsProps) => {
  const singleStore = selectedStores.length === 1 ? selectedStores[0] : null;
  const [lateCancels, setLateCancels] = useState<ClientInvoiceLce[]>([]);
  const [adjustments, setAdjustments] = useState<ClientInvoiceOta[]>([]);
  const [showLateCancelForm, setShowLateCancelForm] = useState(false);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [allAssociatedFdps, setAllAssociatedFdps] = useState<FdpResponse[]>([]);
  const [selectedStoreFdps, setSelectedStoreFdps] = useState<FdpResponse[]>([]);
  const [isOtaLoading, setOtaLoading] = useState<boolean>(false);
  const [isLateCancelsLoading, setLateCancelsLoading] =
    useState<boolean>(false);
  const formikRef = useRef<any>(null); // Reference to Formik instance
  const lateCancelsInitialValues: ClientInvoiceLce = {
    id: 0,
    clientInvoiceId: invoice?.id,
    totalLateCancels: "",
    fdpId: 0,
    fdpName: "",
    operationDate: undefined,
    storePkId: singleStore ? singleStore.id : 0,
    storeId: singleStore ? singleStore.storeId : "",
  };
  const otaInitialValues: ClientInvoiceOta = {
    id: 0,
    otaValue: "",
    operator: "",
    otaDescription: "",
    fdpId: 0,
    fdpName: "",
    operationDate: "",
    clientInvoiceId: invoice?.id,
    adjustmentType: AdjustmentType.CLIENT_INVOICE,
    nextAdjustmentOn: AdjustmentType.CLIENT_INVOICE,
  };

  const getAllOtasForInvoice = useCallback(async () => {
    try {
      setOtaLoading(true);
      const response = await agent.ClientInvoiceAgent.fetchClientInvoiceAllOtas(
        invoice?.id as number
      );
      if (response.isSuccess) {
        setAdjustments(response.data);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "FetchAllOtasException",
      });
    } finally {
      setOtaLoading(false);
    }
  }, [invoice?.id]);

  const getAllLateCancelEditsForInvoice = useCallback(async () => {
    try {
      setLateCancelsLoading(true);
      const response = await agent.ClientInvoiceAgent.fetchClientInvoiceAllLces(
        invoice?.id as number
      );
      if (response.isSuccess) {
        setLateCancels(response.data);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "FetchAllLCEsException",
      });
    } finally {
      setLateCancelsLoading(false);
    }
  }, [invoice?.id]);

  useEffect(() => {
    getAllLateCancelEditsForInvoice();
    getAllOtasForInvoice();
  }, [invoice?.id]);

  useEffect(() => {
    // Combine all unique fdps from each associatedStoreFdps entry
    const allUniqueFdps = Array.from(
      associatedStoreFdps
        .flatMap((store) => store.fdps)
        .reduce((map, fdp) => map.set(fdp.id, fdp), new Map())
        .values()
    );
    setAllAssociatedFdps(allUniqueFdps);
  }, [associatedStoreFdps]);

  useEffect(() => {
    if (singleStore) {
      setSelectedStoreFdps(allAssociatedFdps);
    }
  }, [singleStore, allAssociatedFdps]);

  const handleStoreChange = (storePkId: number) => {
    const selectedStore = selectedStores.find(
      (store) => store.id === storePkId
    );
    // filter FDPs based on selected store for late cancels form.
    const storeFdps =
      associatedStoreFdps.find((store) => store.storePkId === storePkId)
        ?.fdps || [];
    setSelectedStoreFdps(storeFdps);
    if (formikRef.current) {
      formikRef.current.setFieldValue("storePkId", selectedStore?.id || "");
      formikRef.current.setFieldValue("fdpId", 0);
    }
  };

  const lateCancelsValidation = Yup.object().shape({
    totalLateCancels: Yup.number()
      .required("Total cancels are required")
      .min(1, "Provide more than 0"),
    fdpId: Yup.number().required("Fdp is required").min(1, "Fdp is required"),
    storePkId: Yup.number()
      .required("Store is required")
      .min(1, "Store is required"),
  });

  const otaValidation = Yup.object().shape({
    otaValue: Yup.string().required("OTA value is Required"),
    operator: Yup.string().required("Select Operator"),
    otaDescription: Yup.string().required("OTA description is Required"),
  });

  const handleAddLateCancel = async (values: ClientInvoiceLce) => {
    try {
      setLateCancelsLoading(true);
      let formValues = { ...values };
      formValues.operationDate = dayjs(values.operationDate).format(
        "YYYY-MM-DD"
      );
      const response = await agent.ClientInvoiceAgent.addClientInvoiceLce(
        formValues
      );
      if (response.isSuccess) {
        setLateCancelsLoading(false);
        setLateCancels([...lateCancels, response.data]);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddLateCancelException",
      });
    } finally {
      setLateCancelsLoading(false);
      setShowLateCancelForm(false);
    }
  };

  const handleDeleteLateCancel = async (lceId: number) => {
    try {
      setLateCancelsLoading(true);
      const response = await agent.ClientInvoiceAgent.deleteClientInvoiceLce(
        lceId
      );
      if (response.isSuccess) {
        await getAllLateCancelEditsForInvoice();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DeleteLateCancelException",
      });
    } finally {
      setLateCancelsLoading(false);
    }
  };

  const handleAddOta = async (values: ClientInvoiceOta) => {
    try {
      setOtaLoading(true);
      const response = await agent.ClientInvoiceAgent.addClientInvoiceOta(
        values
      );
      if (response.isSuccess) {
        setOtaLoading(false);
        setAdjustments([...adjustments, response.data]);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddOtaException",
      });
    } finally {
      setOtaLoading(false);
      setShowAdjustmentForm(false);
    }
  };

  const handleDeleteOta = async (otaId: number) => {
    try {
      setOtaLoading(true);
      const response = await agent.ClientInvoiceAgent.deleteClientInvoiceOta(
        otaId
      );
      if (response.isSuccess) {
        await getAllOtasForInvoice();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DeleteOtaException",
      });
    } finally {
      setOtaLoading(false);
    }
  };

  return (
    <Box>
      {/* Late Cancels Section */}
      <Typography variant="h5">
        Late Cancels
        <IconButton
          sx={{ ml: 1, color: "primary.main" }}
          onClick={() => setShowLateCancelForm(true)}
        >
          <AddIcon />
        </IconButton>
      </Typography>
      {showLateCancelForm && (
        <Formik
          innerRef={formikRef}
          initialValues={lateCancelsInitialValues}
          validationSchema={lateCancelsValidation}
          onSubmit={handleAddLateCancel}
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Grid mb={1} container spacing={2}>
                <Grid item xs={2}>
                  <FormikTextField
                    name="totalLateCancels"
                    label="Total Late Cancellation"
                    type="number"
                    fullWidth
                    size="small"
                    variant={"outlined"}
                  />
                </Grid>
                {selectedStores.length > 1 ? (
                  <Grid item xs={2}>
                    <FormikTextField
                      name="storePkId"
                      label="Store"
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      onChange={(e: any) => handleStoreChange(e.target.value)}
                    >
                      <MenuItem value={0} disabled>
                        Select Store
                      </MenuItem>
                      {selectedStores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.storeId}
                        </MenuItem>
                      ))}
                    </FormikTextField>
                  </Grid>
                ) : (
                  // If only one store, set it as the selected store in initial values
                  <input
                    type="hidden"
                    name="storePkId"
                    value={singleStore?.id}
                  />
                )}
                <Grid item xs={2}>
                  <FormikTextField
                    name="fdpId"
                    label="FDP"
                    select
                    fullWidth
                    size="small"
                    variant={"outlined"}
                  >
                    <MenuItem value={0} disabled>
                      Select FDP
                    </MenuItem>
                    {selectedStoreFdps.map((fdp: FdpResponse) => (
                      <MenuItem key={fdp.id} value={fdp.id}>
                        {fdp.companyName}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={2}>
                  <FormikDatePicker
                    name="operationDate"
                    label="Date"
                    fullWidth
                    size="small"
                    variant={"outlined"}
                    minDate={invoice?.startDateRange}
                    maxDate={invoice?.endDateRange}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button variant="contained" size="small" type="submit">
                    Save
                  </Button>
                  <Button
                    sx={{ ml: 1 }}
                    variant="contained"
                    onClick={() => setShowLateCancelForm(false)}
                    size="small"
                    type="reset"
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
      {isLateCancelsLoading && (
        <Box
          sx={{
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      {lateCancels.map((lateCancel, index) => (
        <Grid container spacing={2} mb={1}>
          <Grid item xs={2}>
            <FormikTextField
              name="totalLateCancels"
              label="Total Late Cancellation"
              type="number"
              size="small"
              value={lateCancel.totalLateCancels}
              fullWidth
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={2}>
            <FormikTextField
              name="store"
              label="Store"
              fullWidth
              value={lateCancel.storeId}
              size="small"
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={2}>
            <FormikTextField
              name="fdp"
              label="FDP"
              fullWidth
              value={lateCancel.fdpName}
              size="small"
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={2}>
            <FormikTextField
              name="operationDate"
              label="Date"
              fullWidth
              value={
                lateCancel.operationDate
                  ? format(parseISO(lateCancel.operationDate), "MM/dd/yyyy")
                  : ""
              }
              size="small"
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              onClick={() => handleDeleteLateCancel(lateCancel?.id as number)}
              aria-label="delete"
              sx={{ marginLeft: "auto", color: "primary.main" }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      {/* One Time Adjustments Section */}
      <Typography variant="h5" style={{ marginTop: "20px" }}>
        One Time Adjustments
        <IconButton
          sx={{ ml: 1, color: "primary.main" }}
          onClick={() => setShowAdjustmentForm(true)}
        >
          <AddIcon />
        </IconButton>
      </Typography>
      {showAdjustmentForm && (
        <Formik
          initialValues={otaInitialValues}
          validationSchema={otaValidation}
          onSubmit={handleAddOta}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Grid mb={1} container spacing={1}>
                <Grid item xs={1}>
                  <FormikTextField
                    name="operator"
                    label="Operator"
                    select
                    size="small"
                    fullWidth
                    variant={"outlined"}
                  >
                    <MenuItem value={""} disabled>
                      Select Operator
                    </MenuItem>
                    <MenuItem key={"plus"} value={"+"}>
                      <b>+</b>
                    </MenuItem>
                    <MenuItem key={"minus"} value={"-"}>
                      <b>-</b>
                    </MenuItem>
                  </FormikTextField>
                </Grid>
                <Grid item xs={2}>
                  <FormikTextField
                    name="otaValue"
                    label="One-Time Adjustment Value"
                    type="number"
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    variant={"outlined"}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormikTextField
                    name="otaDescription"
                    label="One-Time Adjustment Description"
                    size="small"
                    fullWidth
                    variant={"outlined"}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormikTextField
                    name="fdpId"
                    label="Select FDP"
                    select
                    isDisabled={
                      values.nextAdjustmentOn !== AdjustmentType.FDP_INVOICE
                    }
                    fullWidth
                    size="small"
                    variant={"outlined"}
                  >
                    <MenuItem value={0} disabled>
                      Select FDP
                    </MenuItem>
                    {allAssociatedFdps.map((fdp: FdpResponse) => (
                      <MenuItem key={fdp.id} value={fdp.id}>
                        {fdp.companyName}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Field
                        as={Checkbox}
                        type="checkbox"
                        name="includeInFdpInvoice"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            "nextAdjustmentOn",
                            event.target.checked
                              ? AdjustmentType.FDP_INVOICE
                              : AdjustmentType.CLIENT_INVOICE
                          );
                        }}
                        checked={
                          values.nextAdjustmentOn === AdjustmentType.FDP_INVOICE
                        }
                        sx={{
                          color: "primary.main",
                          mr: -0.5,
                          ml: 2,
                          transform: "scale(1.2)",
                        }}
                      />
                    }
                    label="For FDP Invoice?"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button variant="contained" type="submit" size="small">
                    Save
                  </Button>
                  <Button
                    sx={{ ml: 1 }}
                    variant="contained"
                    onClick={() => setShowAdjustmentForm(false)}
                    size="small"
                    type="reset"
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
      {isOtaLoading && (
        <Box
          sx={{
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      {adjustments.map((ota, index) => (
        <Grid container spacing={2} mb={1}>
          <Grid item xs={1}>
            <FormikTextField
              name="operator"
              label="Operator"
              size="small"
              fullWidth
              variant={"filled"}
              value={ota.operator}
            />
          </Grid>
          <Grid item xs={2}>
            <FormikTextField
              name="otaValue"
              label="One-Time Adjustment Value"
              type="number"
              size="small"
              value={ota.otaValue}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={2}>
            <FormikTextField
              name="fdp"
              label="FDP"
              fullWidth
              value={ota.fdpName}
              size="small"
              variant={"filled"}
            />
          </Grid>
          <Grid item xs={3}>
            <FormikTextField
              name="otaDescription"
              label="One-Time Adjustment Description"
              size="small"
              fullWidth
              variant={"filled"}
              value={ota.otaDescription}
            />
          </Grid>
          {ota.nextAdjustmentOn === AdjustmentType.FDP_INVOICE && (
            <Grid item xs={2}>
              <FormikTextField
                name="nextAdjustMent"
                label="Next AdjustMent On"
                size="small"
                fullWidth
                variant={"filled"}
                value={ota.nextAdjustmentOn}
              />
            </Grid>
          )}
          <Grid item xs={2}>
            <IconButton
              onClick={() => {
                handleDeleteOta(ota.id as number);
              }}
              aria-label="delete"
              sx={{ marginLeft: "auto", color: "primary.main" }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

export default LateCancelsAndAdjustments;
